import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Scale, TrendingDown } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import type { FinancialsGroupDimension, OpportunityCostClassRow } from '@siliconx/shared';
import { SX_PALETTE } from '@siliconx/shared/ui/charts';
import { DataTable } from '@siliconx/shared/ui/DataTable';
import { UsdCell } from '@siliconx/shared/ui/UsdCell';
import { api, throwIfNotOk } from '../../lib/api';
import { formatUsd } from '../../lib/format';
import { FinancialsReportScreen } from '../../components/financials/FinancialsReportScreen';
import { CalculationBasisTrigger } from '../../components/financials/CalculationBasisDrawer';
import { GroupByControl } from '../../components/financials/GroupByControl';
import { qk } from '../../lib/queryKeys';
import { Card } from '@siliconx/shared/ui/Card';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';

/**
 * Opportunity-cost report (SIL-250 T14, spec §4.3 / §7): FMV vs Baseline
 * vs book value, per group, plus the "cost of not deciding" hold-cost
 * narrative (spec §3.3 — opportunity = FMV(med) - Baseline).
 *
 * Epic 3 (lots-first-class) — grouped by asset class, lot, team, or site via
 * the shared `GroupByControl` (one parameterized endpoint, not a tab per
 * dimension — plan Deliverables §1). Point-in-time only.
 */
export const Route = createFileRoute('/financials/opportunity')({
  component: OpportunityCostPage,
});

const DIMENSION_NOUN: Record<FinancialsGroupDimension, string> = {
  class: 'asset class',
  lot: 'lot',
  team: 'team',
  site: 'site',
};

const DIMENSION_NOUN_CAPITALIZED: Record<FinancialsGroupDimension, string> = {
  class: 'Class',
  lot: 'Lot',
  team: 'Team',
  site: 'Site',
};

function OpportunityCostPage() {
  const [dimension, setDimension] = useState<FinancialsGroupDimension>('class');
  const reportQ = useQuery({
    queryKey: qk.financials.opportunity(dimension),
    queryFn: async () => {
      const res = await api.api.client.financials.opportunity.$get({ query: { dimension } });
      await throwIfNotOk(res);
      return res.json();
    },
  });

  return (
    <FinancialsReportScreen
      title="Opportunity Cost"
      pinTo="/financials/opportunity"
      subtitle={`FMV vs Baseline vs book value, by ${DIMENSION_NOUN[dimension]} — the cost of not deciding.`}
      query={reportQ}
      // CSV/PDF export stays class-aggregated regardless of the on-screen
      // grouping (financials-export.ts is unchanged by Epic 3).
      exportCsv={
        dimension === 'class'
          ? { path: '/api/client/financials/export/opportunity.csv', filename: 'financials-opportunity-cost.csv' }
          : undefined
      }
      headerExtra={<GroupByControl value={dimension} onChange={setDimension} />}
      empty={(data) =>
        data.classes.length === 0
          ? {
              icon: Scale,
              title: `No ${dimension === 'class' ? 'asset classes' : `${DIMENSION_NOUN[dimension]}s`} yet`,
              body: 'Once assets have FMV and ITAD-baseline data, opportunity cost will appear here.',
            }
          : null
      }
    >
      {(data) => {
        const classes = data.classes;
        const total = data.totalOpportunityCostUsd;

        return (
          <>
            {/* Headline hero */}
            <Card>
              <div className="flex items-center gap-3">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-amber-50">
                  <TrendingDown className="size-5 text-amber-600" />
                </div>
                <div>
                  <SectionLabel>
                    Total opportunity cost
                  </SectionLabel>
                  <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                    {formatUsd(total)}
                  </p>
                </div>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">
                Estimated value left on the table across the portfolio if every group were disposed
                via ITAD today instead of sold at current market FMV.
              </p>
            </Card>

            {/* FMV vs Baseline vs book, by group */}
            <Card>
              <h2 className="text-[15px] font-semibold text-foreground">FMV vs Baseline vs book</h2>
              <p className="mt-0.5 text-xs text-muted-foreground">Current value by {DIMENSION_NOUN[dimension]}</p>
              <div className="mt-4">
                <OpportunityChart classes={classes} />
              </div>
            </Card>

            {/* Table */}
            <Card>
              <h2 className="mb-4 text-sm font-semibold text-foreground">By {DIMENSION_NOUN[dimension]}</h2>
              <OpportunityTable rows={classes} dimension={dimension} />
            </Card>

            {/* Hold-cost narrative */}
            <div className="space-y-3">
              {classes.map((c) => (
                <HoldCostNarrative key={c.groupId ?? 'unassigned'} row={c} />
              ))}
            </div>
          </>
        );
      }}
    </FinancialsReportScreen>
  );
}

function OpportunityChart({ classes }: { classes: OpportunityCostClassRow[] }) {
  const series = classes.map((c) => ({
    className: c.groupLabel,
    fmv: c.fmvMedUsd,
    itad: c.itadBaselineUsd,
    book: c.netBookValueUsd,
  }));

  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={series} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
        <XAxis dataKey="className" stroke="var(--color-muted-foreground)" fontSize={12} tickLine={false} />
        <YAxis
          stroke="var(--color-muted-foreground)"
          fontSize={12}
          tickLine={false}
          tickFormatter={(v: number) => formatUsd(v, { compact: true })}
          width={56}
        />
        <Tooltip
          contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: 'var(--color-border)' }}
          formatter={(v: number, name: string) => [
            formatUsd(v),
            name === 'fmv' ? 'FMV (med)' : name === 'itad' ? 'Baseline' : 'Net book value',
          ]}
        />
        <Bar dataKey="fmv" fill={SX_PALETTE[0]} radius={[4, 4, 0, 0]} name="fmv" />
        <Bar dataKey="itad" fill={SX_PALETTE[1] ?? SX_PALETTE[0]} radius={[4, 4, 0, 0]} name="itad" />
        <Bar dataKey="book" fill={SX_PALETTE[2] ?? SX_PALETTE[0]} radius={[4, 4, 0, 0]} name="book" />
      </BarChart>
    </ResponsiveContainer>
  );
}

function OpportunityTable({
  rows,
  dimension,
}: {
  rows: OpportunityCostClassRow[];
  dimension: FinancialsGroupDimension;
}) {
  const columns: ColumnDef<OpportunityCostClassRow, unknown>[] = useMemo(
    () => [
      {
        id: 'name',
        header: DIMENSION_NOUN_CAPITALIZED[dimension],
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
            {row.original.groupLabel}
            {row.original.basis && (
              <CalculationBasisTrigger
                title={`${row.original.groupLabel} — Opportunity cost`}
                basis={row.original.basis}
              />
            )}
          </span>
        ),
      },
      {
        id: 'count',
        header: () => <div className="text-right">Assets</div>,
        cell: ({ row }) => (
          <div className="text-right font-num tabular-nums text-foreground">
            {row.original.assetCount.toLocaleString()}
          </div>
        ),
      },
      {
        id: 'book',
        header: () => <div className="text-right">Net book value</div>,
        cell: ({ row }) => (
          <div className="text-right">
            <UsdCell value={row.original.netBookValueUsd} />
          </div>
        ),
      },
      {
        id: 'fmv',
        header: () => <div className="text-right">FMV (med)</div>,
        cell: ({ row }) => (
          <div className="text-right">
            <UsdCell value={row.original.fmvMedUsd} />
          </div>
        ),
      },
      {
        id: 'itad',
        header: () => <div className="text-right">Baseline</div>,
        cell: ({ row }) => (
          <div className="text-right">
            <UsdCell value={row.original.itadBaselineUsd} />
          </div>
        ),
      },
      {
        id: 'opportunity',
        header: () => <div className="text-right">Opportunity cost</div>,
        cell: ({ row }) => {
          const v = row.original.opportunityCostUsd;
          return (
            <div className="text-right">
              <UsdCell
                value={v}
                className={v !== null && v > 0 ? '!text-amber-600' : undefined}
              />
            </div>
          );
        },
      },
    ],
    [dimension],
  );

  return (
    <DataTable
      data={rows}
      columns={columns}
      emptyMessage={`No ${dimension === 'class' ? 'asset classes' : `${DIMENSION_NOUN[dimension]}s`} yet.`}
    />
  );
}

function HoldCostNarrative({ row }: { row: OpportunityCostClassRow }) {
  const { groupLabel, fmvMedUsd, itadBaselineUsd, opportunityCostUsd } = row;

  if (fmvMedUsd === null || itadBaselineUsd === null || opportunityCostUsd === null) {
    return (
      <div className="rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground shadow-sm">
        <span className="font-medium text-foreground">{groupLabel}</span> — not enough FMV or
        ITAD-baseline data to estimate opportunity cost yet.
      </div>
    );
  }

  const narrative =
    opportunityCostUsd > 0
      ? `Selling ${groupLabel} on the open market today would recover ${formatUsd(fmvMedUsd)} vs ${formatUsd(itadBaselineUsd)} through ITAD — holding without a decision leaves an estimated ${formatUsd(opportunityCostUsd)} on the table.`
      : opportunityCostUsd < 0
        ? `${groupLabel} currently recovers more through ITAD (${formatUsd(itadBaselineUsd)}) than open-market FMV (${formatUsd(fmvMedUsd)}) — market sale is not the higher-value path right now.`
        : `${groupLabel} shows no meaningful gap between FMV and Baseline today.`;

  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground shadow-sm">
      {narrative}
    </div>
  );
}
